import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Plus, Loader2, Mail, RefreshCw, X, Copy, CheckCheck,
  Clock, CheckCircle2, XCircle, Ban, Link2, Send
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Amministratore' },
  { value: 'sales_manager', label: 'Resp. Commerciale' },
  { value: 'sales_rep', label: 'Commerciale' },
  { value: 'operator', label: 'Operatore' },
  { value: 'client', label: 'Cliente (Readonly)' },
];

const STATUS_CONFIG = {
  pending: { label: 'In attesa', icon: Clock, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  accepted: { label: 'Accettato', icon: CheckCircle2, cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  expired: { label: 'Scaduto', icon: XCircle, cls: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  cancelled: { label: 'Annullato', icon: Ban, cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.cls}`}>
      <Icon size={9} />{cfg.label}
    </span>
  );
}

function InviteLinkRow({ token, tenantName }) {
  const [copied, setCopied] = useState(false);
  const BASE = window.location.origin;
  const link = `${BASE}/register?token=${token}`;

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success('Link copiato!');
  };

  return (
    <div className="flex items-center gap-2 p-2 bg-muted/40 rounded-lg border border-dashed border-border mt-2">
      <Link2 size={12} className="text-muted-foreground flex-shrink-0" />
      <p className="text-xs font-mono text-muted-foreground flex-1 truncate">{link}</p>
      <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={copy} title="Copia link">
        {copied ? <CheckCheck size={12} className="text-green-500" /> : <Copy size={12} />}
      </Button>
    </div>
  );
}

export default function Invitations() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newInvite, setNewInvite] = useState(null);

  const [form, setForm] = useState({ email: '', role: 'sales_rep', tenant_id: '' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    try {
      const [invRes, tenRes] = await Promise.all([
        axios.get(`${API}/invitations`),
        user?.role === 'super_admin' ? axios.get(`${API}/admin/tenants`) : Promise.resolve({ data: [] }),
      ]);
      setInvites(invRes.data);
      setTenants(tenRes.data);
    } catch {
      toast.error('Errore caricamento inviti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const createInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { email: form.email, role: form.role };
      if (user?.role === 'super_admin' && form.tenant_id) payload.tenant_id = form.tenant_id;
      const res = await axios.post(`${API}/invitations`, payload);
      setNewInvite(res.data);
      setForm({ email: '', role: 'sales_rep', tenant_id: '' });
      setShowForm(false);
      fetchData();
      toast.success('Invito creato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (id) => {
    try {
      const res = await axios.post(`${API}/invitations/${id}/resend`);
      toast.success('Invito aggiornato — copia il nuovo link');
      fetchData();
      return res.data.token;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const cancelInvite = async (id, email) => {
    if (!window.confirm(`Annullare l'invito per "${email}"?`)) return;
    try {
      await axios.delete(`${API}/invitations/${id}`);
      toast.success('Invito annullato');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const filtered = invites.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    if (search && !inv.email.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Inviti</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Gestisci gli inviti di accesso al CRM</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw size={14} className="mr-1.5" />Aggiorna</Button>
          <Button onClick={() => setShowForm(true)} data-testid="new-invite-btn">
            <Plus size={15} className="mr-1.5" />Nuovo Invito
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Cerca per email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-56 h-8 text-sm"
          data-testid="invite-search"
        />
        <div className="flex gap-1.5">
          {[
            { v: 'all', l: 'Tutti' },
            { v: 'pending', l: 'In attesa' },
            { v: 'accepted', l: 'Accettati' },
            { v: 'expired', l: 'Scaduti' },
          ].map(({ v, l }) => (
            <Button key={v} variant={filterStatus === v ? 'default' : 'outline'} size="sm"
              className="h-8 text-xs" onClick={() => setFilterStatus(v)}
              data-testid={`filter-${v}`}>{l}</Button>
          ))}
        </div>
      </div>

      {/* Invitations table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Mail size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nessun invito trovato</p>
              <p className="text-xs mt-1 opacity-60">Crea un nuovo invito con il bottone in alto</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Table header */}
              <div className="hidden md:grid px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/50"
                style={{ gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1fr auto' }}>
                <span>Email</span>
                <span>Ruolo</span>
                <span>Stato</span>
                <span>Creato</span>
                <span>Scadenza</span>
                <span>Azioni</span>
              </div>

              {filtered.map(inv => (
                <div key={inv.id} data-testid={`invite-row-${inv.id}`}>
                  <div className="grid px-4 py-3 items-center gap-2 hover:bg-muted/20"
                    style={{ gridTemplateColumns: '2fr 1fr 1fr 1.5fr 1fr auto' }}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inv.email}</p>
                      {user?.role === 'super_admin' && inv.tenant_name && (
                        <p className="text-xs text-muted-foreground">{inv.tenant_name}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {ROLES.find(r => r.value === inv.role)?.label || inv.role}
                      </span>
                    </div>
                    <div><StatusBadge status={inv.status} /></div>
                    <div className="text-xs text-muted-foreground">
                      {inv.created_at ? new Date(inv.created_at).toLocaleDateString('it-IT') : '—'}
                      {inv.created_by_name && <p className="opacity-70">{inv.created_by_name}</p>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('it-IT') : '—'}
                      {inv.accepted_at && <p className="text-green-600 dark:text-green-400">Accettato {new Date(inv.accepted_at).toLocaleDateString('it-IT')}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      {inv.status === 'pending' && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary"
                            onClick={() => resendInvite(inv.id)} title="Rinnova link"
                            data-testid={`resend-${inv.id}`}>
                            <RefreshCw size={13} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => cancelInvite(inv.id, inv.email)} title="Annulla invito"
                            data-testid={`cancel-${inv.id}`}>
                            <X size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Show link row for pending invites */}
                  {inv.status === 'pending' && inv.token && (
                    <div className="px-4 pb-2">
                      <InviteLinkRow token={inv.token} tenantName={inv.tenant_name} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invite Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2"><Send size={16} />Nuovo Invito</div>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={createInvite} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label>Email destinatario *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="utente@azienda.it"
                required
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ruolo assegnato *</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {user?.role === 'super_admin' && (
              <div className="space-y-1.5">
                <Label>Tenant *</Label>
                <Select value={form.tenant_id} onValueChange={v => setForm(f => ({ ...f, tenant_id: v }))}>
                  <SelectTrigger data-testid="invite-tenant-select"><SelectValue placeholder="Seleziona tenant..." /></SelectTrigger>
                  <SelectContent>
                    {tenants.map(t => (
                      <SelectItem key={t.id || t._id} value={t.id || t._id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700">
              <AlertDescription className="text-xs text-amber-700 dark:text-amber-400">
                Dopo la creazione, copia il link e invialo all'utente. Il link scade in 48 ore.
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="invite-submit">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crea invito
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created invite — show link */}
      <Dialog open={!!newInvite} onOpenChange={() => setNewInvite(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-green-500" />Invito creato!</div>
            </DialogTitle>
          </DialogHeader>
          {newInvite && (
            <div className="space-y-4 mt-2">
              <Alert className="border-green-400 bg-green-50 dark:bg-green-900/10">
                <AlertDescription className="text-sm text-green-700 dark:text-green-400">
                  Copia il link qui sotto e invialo a <strong>{newInvite.email}</strong>.
                </AlertDescription>
              </Alert>
              <InviteLinkRow token={newInvite.token} tenantName={newInvite.tenant_name} />
              <p className="text-xs text-muted-foreground text-center">Il link scade in 48 ore.</p>
              <Button className="w-full" onClick={() => setNewInvite(null)} data-testid="close-invite-dialog">
                Chiudi
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
